import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import analyticsService from "../services/analyticsService";
import { CGV_URL, PRIVACY_POLICY_URL } from "../constants/legal";
import ScreenLayout from "./ui/ScreenLayout";
import { TextField } from "./ui/Field";
import Button from "./ui/Button";
import Icon from "./ui/Icon";
import { buildStationPanel } from "../utils/stationPanel";

export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const LegalLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="font-semibold text-qv-brand underline">
    {children}
  </a>
);

// Écran 1b : l'e-mail seul, sans aucune mention du paiement, pour qu'un abandon
// ici soit imputable à l'e-mail et pas à la carte.
export default function EmailStepComponent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { connector, evcpData } = location.state || {};

  const [email, setEmail] = useState(location.state?.email || "");
  const [accepted, setAccepted] = useState(Boolean(location.state?.email));
  const [touched, setTouched] = useState(false);

  const valid = isValidEmail(email);
  const showError = touched && email !== "" && !valid;

  useEffect(() => {
    analyticsService.pushEvent("email_step_viewed", {
      station_id: evcpData?.reference,
      connector_id: connector?.reference,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = () => {
    if (!valid || !accepted) return;
    analyticsService.pushEvent("email_submitted", { station_id: evcpData?.reference });
    navigate("/payment", { state: { ...location.state, email: email.trim() } });
  };

  return (
    <ScreenLayout
      onBack={() => navigate(-1)}
      step={2}
      panel={buildStationPanel({ t, language: i18n.language, variant: "station", evcpData, connector })}
      footer={
        <Button onClick={handleContinue} disabled={!valid || !accepted}>
          {t("landing.continue")}
        </Button>
      }
    >
      <h1 className="mb-2 text-[28px] leading-9 tracking-[-.02em] font-bold md:text-[32px] md:leading-10">
        {t("emailStep.title")}
      </h1>
      <p className="mb-6 text-[15px] leading-6 text-qv-muted md:mb-7">{t("emailStep.subtitle")}</p>

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          handleContinue();
        }}
      >
        <TextField
          id="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          label={t("emailStep.label")}
          placeholder={t("emailStep.placeholder")}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          onBlur={() => setTouched(true)}
          error={showError ? t("emailStep.invalid") : ""}
        />

        <div className="mt-5 flex flex-col gap-2.5 text-[13px] leading-[19px] font-medium text-qv-muted md:text-sm md:leading-5">
          <p className="flex gap-2.5">
            <Icon name="link-round-linear" size={18} className="text-qv-brand" />
            {t("emailStep.follow")}
          </p>
          <p className="flex gap-2.5">
            <Icon name="user-cross-linear" size={18} className="text-qv-brand" />
            {t("emailStep.noAccount")}
          </p>
          <label className="mt-1.5 flex gap-2.5 items-start cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(event) => setAccepted(event.target.checked)}
              className="mt-px h-[18px] w-[18px] shrink-0 cursor-pointer accent-qv-brand"
            />
            <span>
              <Trans
                i18nKey="emailStep.consent"
                components={[<LegalLink href={CGV_URL} />, <LegalLink href={PRIVACY_POLICY_URL} />]}
              />
            </span>
          </label>
        </div>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </ScreenLayout>
  );
}
